import { Menu } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_STEP } from 'src/graphql/steps.graphql'
import { StepStatus } from 'src/graphql/types/steps'
import IconChevronDown from 'src/icons/IconChevronDown'
import { formatSnakeValue } from 'src/lib/formatters'
import { StepStatusOptions } from 'src/lib/stepStatusOptions'

const colorByStatus = {
  NOT_STARTED: 'gray',
  IN_PROGRESS: 'blue',
  PENDING_REVIEW: 'orange',
  CANCELED: 'red',
  COMPLETED: 'green',
  EXPIRED: 'red',
} as const

interface StepStatusMenuProps {
  stepId: string
  status: StepStatus
  refetchQueries?: any
  /** Badge footprint: 'xs' for the sidebar, 's' (default) for the Documents tab. */
  size?: 'xs' | 's'
  onError?: (error: any) => void
}

/**
 * Status-change dropdown for a hiring step. Renders the step's current status as
 * a colored badge; opening the dropdown lets the user set a new StepStatus,
 * which fires UPDATE_STEP. Shared between the Documents Page Checklist (DQF) tab
 * and the applicant sidebar checklist.
 */
const StepStatusMenu = ({
  stepId,
  status,
  refetchQueries,
  size = 's',
  onError,
}: StepStatusMenuProps) => {
  const statuses = StepStatusOptions.all()

  const [updateStep] = useMutation(UPDATE_STEP, {
    refetchQueries,
    onCompleted: () => {
      toast('DQF updated successfully', 'success')
    },
    onError:
      onError ??
      (() => toast('Unable to update status, please try again.', 'error')),
  })

  return (
    <Menu shadow="md" position="bottom-end">
      <Menu.Target>
        {/* Wrapper div forwards the ref Menu.Target needs (StyledBadge does
            not). No stopPropagation here: callers that render this inside a
            clickable row (e.g. the DQF step row) rely on the click also
            reaching the row so it stays selected. */}
        <div>
          <StyledBadge
            clickable
            compact={size}
            textTransform="capitalize"
            color={colorByStatus[status]}
            rightSection={<IconChevronDown className="h-2.5 w-2.5" />}
          >
            {formatSnakeValue(status)}
          </StyledBadge>
        </div>
      </Menu.Target>
      <Menu.Dropdown className="w-36">
        {statuses.map((option, index) => (
          <Menu.Item
            key={index}
            className="!py-1 !text-xs text-doubleNickel-gray-700"
            onClick={() => {
              updateStep({
                variables: {
                  id: stepId,
                  input: { status: option },
                },
              })
            }}
          >
            {formatSnakeValue(option)}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}

export default StepStatusMenu
